import { CountMetric, EnvironmentDetail, LatencyMetric, PromiseUtils } from '@amzn/ihm-associate-tools-js-lib'
import { AssociateNotificationServiceClient } from '@api/AssociateNotificationServiceClient'
import { StoreFamily } from '@services/StoreService'
import _ from 'lodash'

import { getUserInfo } from '../UserInfoService'
import { getZoneInfo } from '../ZoneModule'

const UpdateIntervalStoreMS = {
    UPDATE_INTERVAL_MENDEL_MS: 5000,
    UPDATE_INTERVAL_KASPIAN_MS: 5000,
    UPDATE_INTERVAL_MS: 10000,
}

let interval: any = null
let currentNotifications = []
const callbacksByNotificationType = new Map()
const genericNotificationCallbacks = []

/**
 * Register a callback for new notifications received, either by push or by pull.
 *
 * @param {(notification) => void} callback: The callback function. The notification object will be passed as an arg.
 * @param {string?} notificationType: An optional string to receive the callback for only specific notificationTypes. Defaults to all notifications if not provided.
 * @returns {void}
 */
export const registerNotificationCallback = (callback: (value: any) => void, notificationType?: string): void => {
    if (notificationType) {
        const callbacksForType = callbacksByNotificationType.get(notificationType) || []
        callbacksForType.push(callback)
        callbacksByNotificationType.set(notificationType, callbacksForType)
    } else {
        genericNotificationCallbacks.push(callback)
    }
}

/**
 * Fetch new notifications from associate notification service.
 *
 * @returns {promise}   Promise resolves with an object that contains a list of open notifications.
 *                      {
 *                          notificationList: [ {notificationObject0}, {notificationObject1}, {notificationObject2} ]
 *                      }
 *                      If there is an error it rejects with the error.
 */
export const updateNotifications = async (): Promise<any> => {
    stopInterval()

    try {
        const userAlias = await getUserAlias()
        const notifications = await pollNotifications(userAlias)
        reconcileNotificationsChanges(currentNotifications, notifications)
    } catch (error) {
        console.error('ATBase: NotificationTaskService: update notification failed.', error)
        return Promise.reject(error)
    } finally {
        stopInterval()
        const currentStoreFamily = EnvironmentDetail.getStoreFamily()
        if (currentStoreFamily === StoreFamily.MENDEL) {
            interval = setTimeout(updateNotifications, UpdateIntervalStoreMS.UPDATE_INTERVAL_MENDEL_MS)
        } else if (currentStoreFamily === StoreFamily.KASPIAN) {
            interval = setTimeout(updateNotifications, UpdateIntervalStoreMS.UPDATE_INTERVAL_KASPIAN_MS)
        } else {
            interval = setTimeout(updateNotifications, UpdateIntervalStoreMS.UPDATE_INTERVAL_MS)
        }
    }
}

/**
 * Return the list of open notifications.
 * @returns {array}   Promise resolves with a list containing open notifications:
 *                    [ {notificationObject0}, {notificationObject1}, {notificationObject2} ]
 *                    If there is an error it rejects with the error.
 */
export const getNotifications = (): any[] => {
    console.log(`ATBase: number of notifs currently polled: ${currentNotifications !== undefined ? currentNotifications.length : 0}`)
    return currentNotifications
}

/**
 * Add a notification to the currently open list. Called by PushNotification to add push notifications that have not been pulled yet.
 * This notification can also be closed, in which case this function will remove it from the open list.
 *
 * @param {object} notification The notification payload.
 */
export const addNotification = (notification): void => {
    const existingNotificationIndex = currentNotifications.findIndex((currentNotification) => {
        return currentNotification.notificationId === notification.notificationId
    })

    if (existingNotificationIndex === -1) {
        console.log('ATBase: adding new notification in currentNotifications - ' + notification.notificationId)
        currentNotifications.push(notification)
        triggerNotificationCallback(notification)
    } else if (currentNotifications[existingNotificationIndex].sequencer < notification.sequencer) {
        console.log('ATBase: notification present but sequencer changed  - ' + notification.notificationId)
        currentNotifications[existingNotificationIndex] = notification
        triggerNotificationCallback(notification)
    }
}

/**
 * Get active user information.
 * @returns {promise}   Promise resolves with an object containing active user information.
 *                      {
 *                          userAlias: "ohuerta"
 *                      }
 *                      If there is an error it rejects with the error.
 */
const getUserAlias = async (): Promise<any> => {
    return getUserInfo()
        .then((userInfo) => {
            console.log('ATBase: notification userInfo: ',JSON.stringify(userInfo))
            return userInfo.userAlias ? userInfo.userAlias : userInfo
        })
        .catch((e) => {
            console.error('ATBase: error when getting user alias', e)
            return Promise.reject(e)
        })
}

const stopInterval = () => {
    if (interval) {
        clearTimeout(interval)
        interval = null
    }
}

const pollNotifications = (userAlias: string) => {
    const zoneInfo = getZoneInfo()

    const faultMetric = new CountMetric(undefined, 'Fault', 'IhmFlyingDutchman', 'NotificationTaskService.updateNotifications')
    const error403Metric = new CountMetric(undefined, '403', 'IhmFlyingDutchman', 'NotificationTaskService.updateNotifications')
    const error4xxMetric = new CountMetric(undefined, '4xx', 'IhmFlyingDutchman', 'NotificationTaskService.updateNotifications')
    const error5xxMetric = new CountMetric(undefined, '5xx', 'IhmFlyingDutchman', 'NotificationTaskService.updateNotifications')
    const latencyMetric = new LatencyMetric(undefined, 'Time', 'IhmFlyingDutchman', 'NotificationTaskService.updateNotifications')
    latencyMetric.start()

    // TODO: https://sim.amazon.com/issues/JIHMSOE-1962 Use token to get user information
    return PromiseUtils.executeWithRetry(() => {
        return getNotificationsFromAssociateNotification(userAlias, zoneInfo.zoneName, zoneInfo.subzoneName)
    }).then((response: any) => {
        latencyMetric.stop()
        if (response.ok) {
            faultMetric.count(0)
            error403Metric.count(0)
            error4xxMetric.count(0)
            error5xxMetric.count(0)

            return response.body.notificationList
        } else {
            if (response.status === 403) {
                error403Metric.count(1)
                error4xxMetric.count(0)
                error5xxMetric.count(0)
            } else if (response.status >= 400 && response.status < 500) {
                error403Metric.count(0)
                error4xxMetric.count(1)
                error5xxMetric.count(0)
            } else if (response.status >= 500) {
                error403Metric.count(0)
                error5xxMetric.count(1)
                error4xxMetric.count(0)
            } else {
                error403Metric.count(0)
                error5xxMetric.count(0)
                error4xxMetric.count(0)
            }
            faultMetric.count(1)
            console.error('ATBase: Call to AssociateNotification failed with response code ' + response.status)
            return currentNotifications
        }
    })
    .catch((error) => {
        faultMetric.count(1)
        console.error('ATBase: failed to fetch Notification service tasks from AssociateNotification ', error)
        return Promise.reject(error)
    })
    .finally(() => {
        latencyMetric.stop()
    })
}

const getNotificationsFromAssociateNotification = (userAlias: string, zoneName: string, subzoneName: string) => {
    return AssociateNotificationServiceClient.getInstance().getNotificationsFromAssociateNotification(EnvironmentDetail.getStoreId(), userAlias, zoneName, subzoneName)
}

const reconcileNotificationsChanges = (oldNotifications, newNotifications) => {
    const oldNotificationIds = _.map(oldNotifications, (notification) => {
        return notification.notificationId
    })
    const newNotificationIds = _.map(newNotifications, (notification) => {
        return notification.notificationId
    })

    const idsInNewButNotOld = _.difference(newNotificationIds, oldNotificationIds)
    currentNotifications = newNotifications

    idsInNewButNotOld.forEach((notificationId) => {
        const newNotification = currentNotifications.find((notification) => {
            return notification.notificationId === notificationId
        })
        console.debug('triggering callback for notification ' + newNotification.notificationId)
        triggerNotificationCallback(newNotification)
    })
}

const triggerNotificationCallback = (notification) => {
    genericNotificationCallbacks.forEach((callback) => {
        callback(notification)
    })

    const callbacksForType = callbacksByNotificationType.get(notification.notificationType)
    if (callbacksForType) {
        callbacksForType.forEach((callback) => {
            callback(notification)
        })
    }
}
